<template>
  <!-- 全站页脚：默认灰底一行；ynrb 见 branding.footer.variant 与 .footer--ynrb -->
  <div class="footer" :class="footerRootClass" :style="footerThemeStyle">
    <div v-if="footerTopDivider" class="footer-line"></div>
    <img v-if="showFooterMain" class="footer-img" :src="footerMainLogo" />
    <span v-if="footerShowMainLabel" class="footer-text">{{
        footerLabelText
      }}</span>
    <span v-if="footerCopyrightText">{{ footerCopyrightText }}</span>
    <span v-else-if="footerShowCopyright" class="footer-copy">版权所有</span>
    <img
        v-if="showFooterMaker"
        class="footer-logo footer-img"
        :src="footerMakerLogo"
    />
    <span v-if="showFooterMaker" class="footer-maker-suffix">制作</span>
  </div>
</template>

<script>
/**
 * @file 全站页脚组件
 * @description 默认主题为单/双小图 + 红字标签；云南日报为双长条图 + 分割线，由 getBranding().footer 驱动。
 */
import { getBranding } from "@/config/branding";
/** 未在 branding 中配置 footerMain 时的兜底（打包内资源） */
import logoSmall from "@/assets/image/common/logo_small.png";
/** 未配置 footerMaker 时的兜底 */
import logoQpg from "@/assets/image/common/logo_qpg.png";

export default {
  name: "Footer",
  computed: {
    /** 页脚第一张图：默认主题为小 logo；云南日报为 footer_01 长条图 */
    footerMainLogo() {
      const logo = getBranding().logos.footerMain;
      if (logo) return logo;
      return logoSmall;
    },
    /** 是否显示页脚主图（logoSmall 兜底时也显示） */
    showFooterMain() {
      const logo = getBranding().logos.footerMain;
      return Boolean(logo);
    },
    /** 页脚第二张图（「制作」左侧）：showMaker 为 false 时不渲染 */
    footerMakerLogo() {
      return getBranding().logos.footerMaker || logoQpg;
    },
    /**
     * 是否渲染顶部分割线（.footer-line）。
     * 优先读 footer.topDivider；未显式配置时，footer.variant === 'ynrb' 视为需要分割线（与原 ynrb 站一致）。
     */
    footerTopDivider() {
      const f = getBranding().footer || {};
      if (typeof f.topDivider === "boolean") {
        return f.topDivider;
      }
      return f.variant === "ynrb";
    },
    /**
     * 根节点附加类名：云南日报启用 .footer--ynrb，覆盖默认灰底与字号（见 style 底部）
     */
    footerRootClass() {
      const f = getBranding().footer || {};
      if (f.variant === "ynrb") {
        return "footer--ynrb";
      }
      if (f.variant === "whb") {
        return "footer--whb";
      }
      if (f.variant === "bjrb") {
        return "footer--bjrb";
      }
      return "";
    },
    /**
     * 是否显示红字标签（如「报业集团」）。云南日报 showMainLabel: false，主图后直接接「版权所有」。
     */
    footerShowMainLabel() {
      const f = getBranding().footer || {};
      return f.showMainLabel !== false;
    },
    /**
     * whb 等主题页脚为整幅长图时已含「版权所有」，可关闭内联「版权所有」文案
     */
    footerShowCopyright() {
      const f = getBranding().footer || {};
      return f.hideInlineCopyright !== true;
    },
    /**
     * 自定义版权文案（完整文案，含主体名称）
     */
    footerCopyrightText() {
      const f = getBranding().footer || {};
      return f.copyrightText || "";
    },
    /** 是否展示第二张图及「制作」后缀：以 footer.showMaker 为准，未配置则看是否存在 footerMaker 资源 */
    showFooterMaker() {
      const b = getBranding();
      const f = b.footer;
      if (f && typeof f.showMaker === "boolean") {
        return f.showMaker;
      }
      return Boolean(b.logos.footerMaker);
    },
    /**
     * 红字标签文案（仅当 footerShowMainLabel 为 true 时展示）；默认「报业集团」
     */
    footerLabelText() {
      const f = getBranding().footer;
      return (f && f.labelText) || "报业集团";
    },
    /**
     * 注入到根节点的 CSS 自定义属性，供默认主题与 .footer--ynrb 共用变量（背景、字色、小屏 logo 高度等）
     */
    footerThemeStyle() {
      const b = getBranding();
      const f = b.footer || {};
      return {
        "--footer-bg": f.bg || "#e5e5e5",
        "--footer-span-color": f.spanColor || "#494949",
        "--footer-label-color": f.labelColor || "#e60012",
        "--footer-label-margin": f.labelMargin || "0 3px 0 2px",
        "--footer-mobile-logo-height": `${f.mobileLogoHeightPx != null ? f.mobileLogoHeightPx : 19}px`,
      };
    },
  },
  data() {
    return {};
  },
  created() {},
  mounted() {},
  beforeDestroy() {},
  methods: {},
};
</script>

<style lang="scss" scoped>

/* 默认页脚：灰底一行，变量来自 footerThemeStyle */
.footer {
  font-size: 17px;
  background: var(--footer-bg, #e5e5e5);
  padding: 6px 0;
  text-align: center;
  vertical-align: middle;
  .footer-img {
    vertical-align: middle;
  }
  .footer-logo {
    height: 24px;
    margin-left: 30px;
  }
  .footer-copy,
  .footer-maker-suffix {
    font-size: 16px;
    font-family: SourceHanSansCN;
    font-weight: 400;
    color: var(--footer-span-color, #494949);
    vertical-align: middle;
  }
  /* 自定义版权文案样式 */
  > span:not(.footer-text):not(.footer-maker-suffix) {
    font-size: 16px;
    font-family: SourceHanSansCN;
    font-weight: 400;
    color: var(--footer-span-color, #494949);
    vertical-align: middle;
    margin-left: 40px;
  }
  .footer-text {
    color: var(--footer-label-color, #e60012);
    font-weight: 900;
    margin: var(--footer-label-margin, 0 3px 0 2px);
    vertical-align: middle;
  }
  .footer-maker-suffix {
    margin-left: 3px;
  }
  @media screen and (max-width: 576px) {
    .footer-img {
      height: var(--footer-mobile-logo-height, 19px);
    }
    .footer-logo {
      margin-left: 15px;
    }
  }

  /* 云南日报：与 ynrb 原站一致，双底部图 + 渐变分割线 + 灰色文案 */
  &.footer--ynrb {
    background: var(--footer-bg, #fff);
    padding: 0 0 28px;
    .footer-line {
      width: 80%;
      max-width: 900px;
      height: 2px;
      background: linear-gradient(to right, white, #cccfde, white);
      margin: 0 auto 28px;
    }
    .footer-img {
      height: 28px;
      vertical-align: middle;
    }
    .footer-logo {
      height: 28px;
      margin-left: 30px;
    }
    .footer-copy,
    .footer-maker-suffix {
      font-size: 18px;
      color: var(--footer-span-color, #afafaf);
      vertical-align: baseline;
    }
    .footer-maker-suffix {
      margin-left: 5px;
    }
    @media screen and (max-width: 768px) {
      height: auto;
      padding: 0 20px 10px;
      line-height: normal;
      .footer-img {
        height: 20px;
      }
      .footer-logo {
        margin-left: 8px;
      }
      .footer-copy,
      .footer-maker-suffix {
        font-size: 14px;
      }
    }
  }

  /* 文汇新民：条图多高页脚就多高；line-height/font-size 归零避免 img 下方空隙 */
  &.footer--whb {
    background: var(--footer-bg, #fff);
    line-height: 0;
    font-size: 0;
    padding: 8px 0 0;
    margin: 0;
    overflow: hidden;
    text-align: center;
    .footer-img {
      display: block;
      width: auto;
      max-width: min(700px, 92vw);
      max-height: 32px;
      height: auto;
      margin: 0 auto;
      padding: 0;
      border: 0;
    }
    @media screen and (max-width: 576px) {
      padding-top: 6px;
      .footer-img {
        max-height: 26px;
        max-width: min(640px, 94vw);
      }
    }
  }

  /* 北京日报：footer_logo.png 整幅长图居中显示 */
  &.footer--bjrb {
    background: var(--footer-bg, #f5f5f5);
    line-height: 0;
    font-size: 0;
    padding: 10px 0;
    margin: 0;
    overflow: hidden;
    text-align: center;
    .footer-img {
      display: inline-block;
      width: auto;
      max-width: min(900px, 90vw);
      height: auto;
      vertical-align: middle;
      border: 0;
    }
    @media screen and (max-width: 576px) {
      padding: 8px 0;
      .footer-img {
        max-width: min(400px, 95vw);
      }
    }
  }
}
</style>
